import { useContext, useEffect, useRef } from 'react';

import { EDIT_PAGE_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const ROVO_AUTO_OPENED_KEY_PREFIX = 'rovo-auto-opened-new-page:';

const getRovoAutoOpenedKey = (contentId: string) => `${ROVO_AUTO_OPENED_KEY_PREFIX}${contentId}`;

/**
 * Opens the Rovo chat mini-modal once the edit-page/load experience succeeds (after VC90/TTAI),
 * but only when the editor was opened for newly-created content.
 *
 * We subscribe to `edit-page/load` (EDIT_PAGE_LOAD_EXPERIENCE), `edit-page/load` fires
 * `taskSuccess` when NCS fully connects — which is after both VC90 and TTAI complete.
 *
 * "New content" is detected by the presence of `draftShareId` in the URL query params at
 * mount time. This query param is added by the create-content flow and is present in the
 * redirect URL sent to the editor after page creation. We capture it once at mount via a ref
 * so that the subscription is immune to any subsequent URL or state changes.
 *
 * User preference is persisted in localStorage keyed by contentId. If the user closes the
 * mini-modal and navigates back to the same draft (even in a new browser session), we will
 * not re-open it. localStorage is preferred over sessionStorage because the user's intent to
 * dismiss ("I closed this") should be respected permanently, not just for the current session.
 *
 * Note: We intentionally do NOT check `getExperienceState().hasStopped` as a fast-path because
 * that state persists across navigations in the singleton ExperienceTracker. A prior page's
 * completed `edit-page/load` experience would appear as `hasStopped = true` when the editor
 * first mounts for a new page, causing the modal to open on the wrong page. Since VC90/TTAI
 * always completes well after the initial render, the subscription is always in place before
 * `taskSuccess` fires.
 */
export const useOpenRovoOnEditPageReady = (contentId: string, isEnabled: boolean) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { getQueryParams } = useContext(RoutesContext);
	const { openConversationAssistant } = useToggleConversationAssistant();

	// Capture at mount whether this is a newly-created page (draftShareId in URL)
	// AND whether we haven't already auto-opened for this contentId.
	// Using a ref ensures this is immune to URL/state changes after initial mount.
	const { draftShareId } = getQueryParams();
	const shouldOpenRef = useRef(
		isEnabled && !!draftShareId && !localStorage.getItemAsBoolean(getRovoAutoOpenedKey(contentId)),
	);
	// Guard: only open once, even across re-renders.
	const hasOpenedRef = useRef(false);

	useEffect(() => {
		// Only auto-open for newly-created content that hasn't been auto-opened before.
		if (!shouldOpenRef.current) {
			return;
		}

		// Subscribe and open once the edit-page/load experience succeeds (after VC90/TTAI).
		return experienceTracker.subscribe((event) => {
			if (
				!hasOpenedRef.current &&
				event.name === EDIT_PAGE_LOAD_EXPERIENCE &&
				event.action === 'taskSuccess'
			) {
				hasOpenedRef.current = true;
				// Persist that we've auto-opened for this content so we don't re-open
				// if the user dismisses and navigates back to the same draft.
				localStorage.setItem(getRovoAutoOpenedKey(contentId), true);
				openConversationAssistant('mini-modal');
			}
		});
	}, [experienceTracker, openConversationAssistant, contentId]);
};
